import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, CheckCircle2, ShieldCheck, Terminal } from 'lucide-react';

const CODE_LENGTH = 8;

function normalizeCode(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, CODE_LENGTH);
}

function toCodeChars(value) {
  const normalized = normalizeCode(value);
  return Array.from({ length: CODE_LENGTH }, (_, index) => normalized[index] || '');
}

function toUserCode(chars) {
  const normalized = normalizeCode(chars.join(''));
  if (normalized.length < CODE_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, 4)}-${normalized.slice(4)}`;
}

function DeviceApproval() {
  const location = useLocation();
  const initialUserCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('user_code') || '').toUpperCase();
  }, [location.search]);

  const [chars, setChars] = useState(() => toCodeChars(initialUserCode));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const inputRefs = useRef([]);
  const shortToken = sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');

  const userCode = toUserCode(chars);
  const isComplete = normalizeCode(chars.join('')).length === CODE_LENGTH;

  const approve = async (code) => {
    setError('');
    setResult(null);

    if (!shortToken) {
      setError('Debes iniciar sesión para aprobar este dispositivo.');
      return false;
    }

    if (!isComplete) {
      setError('Introduce los 8 caracteres del user_code.');
      return false;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        '/api/device/approve',
        { user_code: String(code).trim().toUpperCase() },
        {
          headers: {
            Authorization: `Bearer ${shortToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setResult(response.data || { success: true });
      localStorage.setItem('deviceApproval.lastCode', String(code).trim().toUpperCase());
      localStorage.setItem('deviceApproval.lastStatus', response.data?.status || 'approved');
      return true;
    } catch (requestError) {
      setError(requestError?.response?.data?.details || requestError?.response?.data?.error || requestError.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (event) => {
    event.preventDefault();
    await approve(userCode);
  };

  const handleChangeChar = (index, rawValue) => {
    const nextChar = normalizeCode(rawValue).slice(-1);
    setChars((previous) => {
      const next = [...previous];
      next[index] = nextChar || '';
      return next;
    });

    if (nextChar && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !chars[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = normalizeCode(event.clipboardData.getData('text'));
    if (!pasted) {
      return;
    }

    setChars(toCodeChars(pasted));
    const targetIndex = Math.min(pasted.length, CODE_LENGTH) - 1;
    inputRefs.current[Math.max(targetIndex, 0)]?.focus();
  };

  useEffect(() => {
    setChars(toCodeChars(initialUserCode));
  }, [initialUserCode]);

  useEffect(() => {
    const normalizedCode = normalizeCode(initialUserCode);
    if (normalizedCode.length !== CODE_LENGTH) {
      return;
    }

    const displayCode = `${normalizedCode.slice(0, 4)}-${normalizedCode.slice(4)}`;
    const lastCode = localStorage.getItem('deviceApproval.lastCode');
    const lastStatus = localStorage.getItem('deviceApproval.lastStatus');
    if (lastCode === displayCode && (lastStatus === 'approved' || lastStatus === 'already_approved')) {
      setResult({ success: true, status: lastStatus });
      return;
    }

    if (shortToken) {
      approve(displayCode);
    }
  }, [initialUserCode, shortToken]);

  return (
    <div
      className="min-h-screen px-4 py-10"
      style={{
        background:
          'radial-gradient(1200px 400px at 12% 100%, rgba(236,72,153,.22), transparent 55%), radial-gradient(1200px 420px at 88% 0%, rgba(59,130,246,.22), transparent 55%), #eef2ff',
      }}
    >
      <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-900/10 sm:p-8">
        <div className="mb-5 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
            <ShieldCheck size={20} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Aprobar Dispositivo</h1>
            <p className="text-sm text-slate-500">OAuth Device Flow para `citaspro-cli`</p>
          </div>
        </div>

        <p className="mb-6 text-sm text-slate-600">
          Introduce el código mostrado en el CLI para autorizar al agente y habilitar tools de CitasPro.
        </p>

        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Terminal size={14} />
            CLI: `npm i -g citaspro-cli`
          </div>
          <div className="mt-1 text-xs text-slate-500">Comando típico: `citasproctl auth login`</div>
        </div>

        <form onSubmit={handleApprove} className="space-y-5">
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Enter the 8-char code</p>
            <div className="flex items-center gap-2" onPaste={handlePaste}>
              {chars.map((char, index) => (
                <React.Fragment key={`slot-${index}`}>
                  {index === 4 ? <span className="px-1 text-xl text-slate-400">-</span> : null}
                  <input
                    ref={(element) => {
                      inputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="text"
                    maxLength={1}
                    value={char}
                    onChange={(event) => handleChangeChar(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    className={`h-12 w-11 rounded-lg border text-center text-xl font-semibold uppercase tracking-wide outline-none transition ${
                      char
                        ? 'border-blue-400 bg-blue-50 text-slate-900'
                        : 'border-slate-300 bg-white text-slate-500 focus:border-blue-400 focus:bg-blue-50'
                    }`}
                  />
                </React.Fragment>
              ))}
            </div>
            <p className={`mt-2 text-xs ${isComplete ? 'text-emerald-600' : 'text-slate-500'}`}>
              {isComplete ? `Codigo listo: ${userCode}` : 'Completa los 8 caracteres para continuar.'}
            </p>
          </div>

          {error ? (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <AlertCircle size={16} className="mt-0.5" />
              <span>{error}</span>
            </div>
          ) : null}

          {result?.success ? (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 size={16} className="mt-0.5" />
              <span>
                Dispositivo vinculado correctamente.
                {result?.status === 'already_approved' ? ' Este código ya estaba aprobado.' : ''} Ya puedes volver al CLI y continuar.
              </span>
            </div>
          ) : null}

          <div className="flex gap-3 pt-1">
            <a
              href="/"
              className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-center text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Do It Later
            </a>
            <button
              type="submit"
              disabled={loading || !isComplete}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {loading ? 'Aprobando...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeviceApproval;
